import type { HierarcicalObjectReference } from "@novorender/data-js-api";

import { byProperty, type ObjectDataAccess, type PropertyQuery } from "dataAccess";

import { getFilePathFromObjectPath, isLeafNode } from "./objectData";
import { SelectionSet } from "./selectionSet";

/**
 * Extension-less federated 2D files: Drawings-tool sheets (`drawings`) and
 * Documents-tool files (`DOCUMENTS`). Object-db equality is case-sensitive, so
 * both casings seen in scenes are listed; the per-object fallback is case-insensitive.
 */
const DOCUMENT_SOURCE_QUERY: PropertyQuery = byProperty("Procore/DocumentSource", [
    "drawings",
    "DOCUMENTS",
    "documents",
]);

/** Sheets whose path still carries a `.pdf` extension — a substring, so `contains`. */
const PDF_PATH_QUERY: PropertyQuery = { terms: [{ property: "path", contains: ".pdf" }] };

/**
 * PDF/drawing sheet ids for the current scene.
 *
 * Procore does not nest 2D files in folders, so two scene-wide queries are enough: one
 * on `Procore/DocumentSource`, one on path for `.pdf`. They run in parallel and are
 * merged; consumers never observe a partial set.
 *
 * Each query matches on its own terms, so hits are kept only when the path also satisfies
 * the rule `isPdfOrDrawingObjectId` (`dataAccess/utils`) applies: the set gates 3D
 * selection, and an id in it is never read again.
 *
 * **Known gap (BM-13779).** {@link pathAllowsDocument} admits only nodes reported as
 * leaves, and the federated file roots in the investigated scenes are `NodeType.Internal`
 * carrying no `descendants` — 2D document and extension-less 3D container alike. Those ids
 * stay out of this set, so the reset flyout keeps counting them, the object tree keeps
 * their row and its partially-hidden hint, and a canvas pick still resolves them as 3D.
 * Only leaf-typed document nodes are covered; see the gap tests in
 * `features/modelTree/__tests__/utils.test.ts`.
 *
 * Rejects on an abort rather than answering short, against the data-access contract of
 * answering one with an empty value: a silently truncated set reads as "these are all the
 * sheets", and every id missing from it turns a drawing click into a 3D pick. Hence the
 * re-check after the queries — an aborted iterator simply stops yielding.
 */
export async function discoverPdfObjectIds(access: ObjectDataAccess, abortSignal?: AbortSignal): Promise<SelectionSet> {
    abortSignal?.throwIfAborted();
    const [fromSource, fromPath] = await Promise.all([
        collectMatchingIds(access, DOCUMENT_SOURCE_QUERY, pathAllowsDocument, abortSignal),
        collectMatchingIds(access, PDF_PATH_QUERY, hasPdfFilePath, abortSignal),
    ]);
    abortSignal?.throwIfAborted();
    return fromSource.withAdded(fromPath);
}

/** The file the path names, or `null` when it names none — the extension-less federated case. */
function filePathOf(object: HierarcicalObjectReference): string | null {
    return object.path ? getFilePathFromObjectPath(object.path) : null;
}

/**
 * `DocumentSource` only decides for leaf paths that name no file (or a `.pdf`); a hit
 * whose path names a non-PDF file is not a document.
 *
 * The `isLeafNode` gate is meant to keep a federated model root that carries the stamp out
 * of the set, but it is keyed on node *type* rather than on having children, so it also
 * rejects the Internal, childless document roots of BM-13779. A model whose path names its
 * format (`.nwd`, `.ifc`, …) is already excluded by the path rule below, so the gate only
 * changes the answer for the extension-less hash case — where a 2D document and a 3D
 * container are, on every property we can read, the same node.
 */
function pathAllowsDocument(object: HierarcicalObjectReference): boolean {
    if (!isLeafNode(object)) {
        return false;
    }
    const filePath = filePathOf(object);
    return filePath === null || filePath.toLowerCase().endsWith(".pdf");
}

/** The `.pdf` is the file itself, not a substring of some folder along the way. */
function hasPdfFilePath(object: HierarcicalObjectReference): boolean {
    return filePathOf(object)?.toLowerCase().endsWith(".pdf") ?? false;
}

async function collectMatchingIds(
    access: ObjectDataAccess,
    query: PropertyQuery,
    filter: (object: HierarcicalObjectReference) => boolean,
    abortSignal: AbortSignal | undefined,
): Promise<SelectionSet> {
    const ids = new Set<number>();
    for await (const hit of access.findByProperties(query, { signal: abortSignal })) {
        abortSignal?.throwIfAborted();
        if (filter(hit)) {
            ids.add(hit.id);
        }
    }
    return SelectionSet.fromIds(ids);
}
